import {
  ErrorsEnum,
  InstanceDestroying,
  InstanceExpired,
  InstanceNotFound,
} from './errors/index.js';
import {
  ServiceLocatorInstanceHolder,
  ServiceLocatorInstanceHolderStatus,
} from './ServiceLocatorInstanceHolder.js';
import type { ServicesConfig, ServicesInstancesNames } from './types.js';

export class ServiceLocatorManager<Services extends ServicesConfig = {}> {
  private readonly instancesHolders: Map<
    ServicesInstancesNames<Services>,
    ServiceLocatorInstanceHolder<Services>
  > = new Map();

  constructor(private readonly logger: Console | null = null) {}

  /**
   * Returns a tuple with possible errors and instance holder
   *
   * If there is no instance holder with the given name, returns [InstanceNotFound]
   * If the instance holder is expired, returns [InstanceExpired, holder]
   * If the instance holder is destroying, returns [InstanceDestroying, holder]
   * Otherwise, returns [undefined, holder]
   */
  get<K extends ServicesInstancesNames<Services>>(
    name: K,
  ):
    | [
        InstanceExpired | InstanceDestroying,
        ServiceLocatorInstanceHolder<Services>,
      ]
    | [InstanceNotFound]
    | [undefined, ServiceLocatorInstanceHolder<Services>] {
    const holder = this.instancesHolders.get(name);
    if (holder) {
      if (holder.ttl !== Infinity) {
        const now = Date.now();
        if (now - holder.createdAt > holder.ttl) {
          this.logger?.log(
            `[ServiceLocatorManager]#getInstanceHolder() TTL expired for ${holder.name}`,
          );
          return [new InstanceExpired(holder.name), holder];
        }
      } else if (
        holder.status === ServiceLocatorInstanceHolderStatus.Destroying
      ) {
        this.logger?.log(
          `[ServiceLocatorManager]#getInstanceHolder() Instance ${holder.name} is destroying`,
        );
        return [new InstanceDestroying(holder.name), holder];
      }

      return [undefined, holder];
    } else {
      this.logger?.log(
        `[ServiceLocatorManager]#getInstanceHolder() Instance ${name} not found`,
      );
      return [new InstanceNotFound(name)];
    }
  }

  set<K extends ServicesInstancesNames<Services>>(
    name: K,
    holder: ServiceLocatorInstanceHolder<Services>,
  ): void {
    this.instancesHolders.set(name, holder);
  }

  has<K extends ServicesInstancesNames<Services>>(
    name: K,
  ): [InstanceExpired | InstanceDestroying] | [undefined, boolean] {
    const [error, holder] = this.get(name);
    if (!error) {
      return [undefined, true];
    }
    if (
      [ErrorsEnum.InstanceExpired, ErrorsEnum.InstanceDestroying].includes(
        error.code,
      )
    ) {
      return [error];
    }
    return [undefined, !!holder];
  }

  delete<K extends ServicesInstancesNames<Services>>(name: K): boolean {
    return this.instancesHolders.delete(name);
  }

  filter(
    predicate: (
      value: ServiceLocatorInstanceHolder<Services>,
      key: ServicesInstancesNames<Services>,
    ) => boolean,
  ): Map<
    ServicesInstancesNames<Services>,
    ServiceLocatorInstanceHolder<Services>
  > {
    return new Map(
      [...this.instancesHolders].filter(([key, value]) =>
        predicate(value, key),
      ),
    );
  }
}
